import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/contexts/AuthContext";

const EMPTY_TABLE = () => ({ name: "New table", headers: ["Term", "Cost"], cost_columns: [1], rows: [["", ""]], orientation: "portrait" });

// In-place editor for an uploaded rate card (admin only). Edits the raw tables
// stored on the product — admin fee + markup are still applied on top at render.
export function RateCardEditor({ productId, open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [tables, setTables] = useState(null);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTables(null);
    api.get(`/admin/products/${productId}/rate-tables`).then(({ data }) => {
      setName(data.name || "");
      setTables(data.rate_tables || []);
      setActive(0);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [open, productId]);

  const t = tables?.[active];
  const patch = (fn) => setTables((ts) => ts.map((x, i) => (i === active ? fn({ ...x, headers: [...x.headers], rows: x.rows.map((r) => [...r]), cost_columns: [...(x.cost_columns || [])] }) : x)));

  const setHeader = (ci, v) => patch((x) => { x.headers[ci] = v; return x; });
  const setCell = (ri, ci, v) => patch((x) => { x.rows[ri][ci] = v; return x; });
  const toggleCost = (ci) => patch((x) => { x.cost_columns = x.cost_columns.includes(ci) ? x.cost_columns.filter((c) => c !== ci) : [...x.cost_columns, ci].sort((a, b) => a - b); return x; });
  const addRow = () => patch((x) => { x.rows.push(x.headers.map(() => "")); return x; });
  const delRow = (ri) => patch((x) => { x.rows.splice(ri, 1); return x; });
  const moveRow = (ri, d) => patch((x) => { const j = ri + d; if (j < 0 || j >= x.rows.length) return x; [x.rows[ri], x.rows[j]] = [x.rows[j], x.rows[ri]]; return x; });
  const addCol = () => patch((x) => { x.headers.push("New column"); x.rows.forEach((r) => r.push("")); x.cost_columns.push(x.headers.length - 1); return x; });
  const delCol = (ci) => patch((x) => { x.headers.splice(ci, 1); x.rows.forEach((r) => r.splice(ci, 1)); x.cost_columns = x.cost_columns.filter((c) => c !== ci).map((c) => (c > ci ? c - 1 : c)); return x; });
  const addTable = () => { setTables((ts) => [...ts, EMPTY_TABLE()]); setActive(tables.length); };
  const delTable = () => { if (!window.confirm(`Remove table "${t.name}"?`)) return; setTables((ts) => ts.filter((_, i) => i !== active)); setActive(0); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${productId}/rate-tables`, { name, rate_tables: tables });
      toast.success("Rate card saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="rate-card-editor">
        <DialogHeader><DialogTitle>Edit rate card</DialogTitle></DialogHeader>
        {!tables ? <div className="text-sm text-[#6C757D]">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Program name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="rce-name" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="rce-tabs">
              {tables.map((x, i) => (
                <button key={i} type="button" onClick={() => setActive(i)} className={`px-2.5 py-1.5 rounded-sm text-xs font-semibold border ${i === active ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white border-[#E9ECEF] text-[#0B132B]"}`} data-testid={`rce-tab-${i}`}>{x.name || `Table ${i + 1}`}</button>
              ))}
              <button type="button" onClick={addTable} className="px-2 py-1.5 rounded-sm text-xs font-semibold border border-dashed border-[#6C757D]/50 text-[#6C757D] hover:text-[#0B132B]" data-testid="rce-add-table"><Plus size={12} className="inline" /> table</button>
            </div>
            {t && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Table title</Label>
                    <Input value={t.name} onChange={(e) => patch((x) => { x.name = e.target.value; return x; })} className="rounded-sm h-9 mt-1" data-testid="rce-table-name" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Eligibility / note (prints above the table)</Label>
                    <Input value={t.note_inline || ""} onChange={(e) => patch((x) => { x.note_inline = e.target.value || undefined; return x; })} className="rounded-sm h-9 mt-1" data-testid="rce-table-note" />
                  </div>
                  <Button variant="outline" size="sm" onClick={delTable} className="h-9 rounded-sm text-red-700 border-red-200" data-testid="rce-del-table"><Trash2 size={12} className="mr-1" /> Remove table</Button>
                </div>
                <div className="text-[11px] text-[#6C757D]">Tick <b>$</b> under a column to mark it as a rate column (admin fee + markup get added to it). Blank cells stay blank.</div>
                <div className="overflow-x-auto border border-[#E9ECEF] rounded-sm">
                  <table className="text-xs min-w-full">
                    <thead>
                      <tr className="bg-[#0B132B] text-white">
                        <th className="w-16" />
                        {t.headers.map((h, ci) => (
                          <th key={ci} className="px-1 py-1 text-left align-top min-w-[120px]">
                            <input value={h} onChange={(e) => setHeader(ci, e.target.value)} className="w-full bg-white/10 text-white placeholder-white/50 rounded-sm px-1.5 py-1 font-bold outline-none focus:bg-white/20" data-testid={`rce-header-${ci}`} />
                            <div className="flex items-center justify-between mt-1 text-[10px]">
                              <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={t.cost_columns.includes(ci)} onChange={() => toggleCost(ci)} data-testid={`rce-cost-${ci}`} /> $</label>
                              <button type="button" onClick={() => delCol(ci)} className="text-white/60 hover:text-red-300" title="Remove column" data-testid={`rce-del-col-${ci}`}><Trash2 size={10} /></button>
                            </div>
                          </th>
                        ))}
                        <th className="px-1 align-top"><button type="button" onClick={addCol} className="text-[10px] text-[#D4AF37] font-bold whitespace-nowrap" data-testid="rce-add-col"><Plus size={10} className="inline" /> col</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((r, ri) => (
                        <tr key={ri} className={ri % 2 ? "bg-[#F8F9FA]" : "bg-white"}>
                          <td className="px-1 whitespace-nowrap">
                            <button type="button" onClick={() => moveRow(ri, -1)} className="text-[#6C757D] hover:text-[#0B132B] p-0.5"><ArrowUp size={10} /></button>
                            <button type="button" onClick={() => moveRow(ri, 1)} className="text-[#6C757D] hover:text-[#0B132B] p-0.5"><ArrowDown size={10} /></button>
                            <button type="button" onClick={() => delRow(ri)} className="text-[#6C757D] hover:text-red-600 p-0.5" data-testid={`rce-del-row-${ri}`}><Trash2 size={10} /></button>
                          </td>
                          {r.map((c, ci) => (
                            <td key={ci} className="px-1 py-0.5 border-t border-[#E9ECEF]">
                              <input value={c ?? ""} onChange={(e) => setCell(ri, ci, e.target.value)} className={`w-full rounded-sm border border-transparent hover:border-[#E9ECEF] focus:border-[#D4AF37] px-1.5 py-1 outline-none bg-transparent ${t.cost_columns.includes(ci) ? "text-right font-semibold" : ""}`} data-testid={`rce-cell-${ri}-${ci}`} />
                            </td>
                          ))}
                          <td />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button variant="outline" size="sm" onClick={addRow} className="h-8 rounded-sm" data-testid="rce-add-row"><Plus size={12} className="mr-1" /> Add row</Button>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving || !tables} className="rounded-sm bg-[#0B132B] text-white" data-testid="rce-save">
            {saving ? <Loader2 className="animate-spin mr-1.5" size={12} /> : <Save size={12} className="mr-1.5" />} Save rate card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
